import { useAuth0, User } from "@auth0/auth0-react";
import _ from "lodash";
import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";

import { InfoResponse } from "@/@types/growthos-api/schema/openapi";
import { PageLoader } from "@/Components";
import AccessDeniedPage from "@/Components/v2/pages/Error/AccessDeniedPage";
import NoCustomerDataPage from "@/Components/v2/pages/Error/NoCustomerDataPage";
import { queryClient } from "@/lib/react-query";
import { resetAllStores } from "@/lib/store";
import { useNestJsInfo } from "@/shared/api/getInfo";
import { useUpdateTheme } from "@/shared/api/updateTheme";
import { EmailVerification } from "@/shared/components/EmailVerification";
import { Nodes } from "@/shared/utils/nodes";
import { tenantIdStorage } from "@/storage/tenantIdStorage";
import { Tree } from "@/Utils/Collections";
import { getCurrentTheme, Theme } from "@/Utils/Colors/Theme";
import { createAllGraphsColor } from "@/Utils/GetGraphColor";
import { handleConfigMapping, mapping } from "@/Utils/mapping-utils";

type InfoData = {
  loading: boolean;
  initInfo: InfoResponse["infoData"];
  userData: InfoResponse["userData"] | undefined;
  configData: InfoResponse["configData"] | undefined;
  currentOrganization: InfoResponse["currentOrganization"] | undefined;
  organizationMemberships: InfoResponse["organizationMemberships"];
  theme: Theme;
  tree: Tree;
  clientName: string;
  nodes: Nodes | null;
  intercomUserJwt: string | undefined;
  switchAccount: (account: InfoResponse["currentOrganization"]) => Promise<void>;
  changeTheme: () => Promise<void>;
  auth0User: User | undefined;
};

const InfoContext = createContext<InfoData | undefined>(undefined);

type InfoProviderProps = {
  children: ReactNode;
};

export const InfoProvider: React.FC<InfoProviderProps> = ({ children }) => {
  const {
    isAuthenticated,
    error: auth0Error,
    isLoading: auth0Loading,
    user,
    loginWithRedirect,
  } = useAuth0();
  const { data, isLoading, error, refetch } = useNestJsInfo({
    config: {
      enabled: isAuthenticated,
    },
  });
  const [isReloading, setIsReloading] = useState(false);

  if (data?.configData?.mapping) {
    handleConfigMapping(data.configData.mapping);
  }

  const brandSet = new Set();

  const treeData =
    data?.infoData?.hierarchy.map((node) => ({
      ...node,
      layer: node.layer || [],
      extras: node.extras as Record<string, string | number | boolean | null>,
      data: undefined,
    })) ?? [];

  const tree = Tree.fromJSON({ node: treeData });

  if (data?.configData?.mapping?.meta) {
    tree.addNameMapping(data.configData.mapping.meta, data.configData.mapping.nodeMetaLayer);
  }

  tree.node.forEach((node) => {
    brandSet.add(node.extras.mappedName);
  });

  const shouldHidePortfolio = data?.configData?.dashboard?.hidePortfolio;
  const nodeData =
    data?.infoData?.hierarchy.map((node) => ({
      ...node.data,
    })) || [];

  const nodes = new Nodes(nodeData, tree, shouldHidePortfolio);

  const clientName = Object.keys(data?.infoData?.hierarchy || {});

  const [currentTheme, setCurrentTheme] = useState<Theme>({
    mode: "light",
    color: "black",
  });

  // save user info
  if (data?.userData) {
    localStorage.setItem("user", JSON.stringify(data.userData));
  }

  useEffect(() => {
    if (data?.userData) {
      setCurrentTheme(getCurrentTheme(data.userData.theme || "light"));
    }
  }, [data?.userData]);

  const tenantId = data?.currentOrganization?.insightsTenantId;

  if (tenantId) {
    tenantIdStorage.update(tenantId);
  }

  if (data?.infoData?.channel) {
    createAllGraphsColor({
      channels: mapping(data.infoData.channel || []),
      brands: mapping(Array.from(brandSet)),
    });
  }

  const isInitInfoEmpty = (initInfo?: InfoData["initInfo"]) =>
    _.isEmpty(initInfo?.channel) && _.isEmpty(initInfo?.hierarchy) && _.isEmpty(initInfo?.layers);

  const noCustomerData = isInitInfoEmpty(data?.infoData);

  const { mutateAsync: updateTheme } = useUpdateTheme();

  if (isLoading || auth0Loading || isReloading) return <PageLoader themeMode={currentTheme.mode} />;
  if (auth0Error) return <AccessDeniedPage />;
  if (error) return <AccessDeniedPage error={error} />;
  if (!isAuthenticated || !user) {
    loginWithRedirect({
      appState: { returnTo: window.location.pathname + window.location.search },
    });

    return <PageLoader themeMode={currentTheme.mode} />;
  }

  if (!user?.email_verified) {
    return <EmailVerification />;
  }

  if (noCustomerData) {
    return <NoCustomerDataPage />;
  }

  return (
    <InfoContext.Provider
      value={{
        loading: isLoading || isReloading,
        initInfo: {
          channel: data?.infoData?.channel || [],
          hierarchy: data?.infoData?.hierarchy || [],
          layers: data?.infoData?.layers || [],
          defaultNodeName: data?.infoData?.defaultNodeName || "",
          geographies: data?.infoData?.geographies || [],
          latestTimelineId: data?.infoData?.latestTimelineId || "",
        },
        tree,
        clientName: clientName[0] || "",
        userData: data?.userData,
        configData: data?.configData,
        currentOrganization: data?.currentOrganization,
        organizationMemberships: data?.organizationMemberships || [],
        theme: currentTheme,
        nodes: nodes,
        intercomUserJwt: data?.intercomUserJwt,
        auth0User: user,
        switchAccount: async (account: InfoResponse["currentOrganization"]) => {
          setIsReloading(true);
          tenantIdStorage.update(account.insightsTenantId ?? "");

          resetAllStores();

          sessionStorage.clear();
          localStorage.clear();

          await queryClient.invalidateQueries();
          queryClient.removeQueries();
          await queryClient.clear();
          queryClient.resetQueries();
          queryClient.cancelQueries();

          refetch();

          window.location.href = "/";
          setIsReloading(false);
        },
        changeTheme: async () => {
          const newMode = currentTheme.mode === "light" ? "dark" : "light";

          await updateTheme(newMode);
          const updatedTheme = getCurrentTheme(newMode);
          setCurrentTheme(updatedTheme);
        },
      }}
    >
      {children}
    </InfoContext.Provider>
  );
};

export const useInfo = () => {
  const context = useContext(InfoContext);

  if (context === undefined) {
    throw new Error("useInfo must be used within an InfoProvider");
  }

  return context;
};
